import { useState } from 'react'
import { FaPlusCircle, FaMinusCircle, FaEdit } from 'react-icons/fa'
import { toast } from 'react-toastify'
import { InputDiv, Select, ActionButtonsRow } from '../../../styles/global'
import { AddButton, TableContainer, Table, Td, Th, Tr } from './styles'

const MAX_BANDS = 100
const MAX_RULES = 100

const RULE_TYPES = [
  { value: 'quantity', label: 'Por quantidade de aparelhos' },
  { value: 'power-bands', label: 'Por faixas progressivas de potência' },
]

const emptyForm = { name: '', type: 'quantity', unit: 'kW', bands: [{ upTo: '', factor: '1' }] }

const bandInputStyle = { width: '100%', padding: '8px', fontSize: '16px', boxSizing: 'border-box' }

function slugify(text) {
  return text
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function generateRuleId(name, rules) {
  const base = slugify(name).slice(0, 90) || 'regra'
  let id = base
  let suffix = 2

  while (rules.some((rule) => rule.id === id)) {
    id = `${base}-${suffix}`
    suffix += 1
  }

  return id
}

function formFromRule(rule) {
  return {
    name: rule.name || rule.id,
    type: rule.type,
    unit: rule.unit || 'kVA',
    bands: rule.bands.map((band) => ({
      upTo: band.upTo === null ? '' : String(band.upTo),
      factor: String(band.factor),
    })),
  }
}

function validateForm(form) {
  const name = form.name.trim()

  if (!name) return 'Informe o nome da regra.'
  if (name.length > 120) return 'O nome da regra deve ter até 120 caracteres.'
  if (form.bands.length > MAX_BANDS) return `Use no máximo ${MAX_BANDS} faixas.`

  let previous = 0

  for (let i = 0; i < form.bands.length; i++) {
    const band = form.bands[i]
    const isLast = i === form.bands.length - 1
    const factor = band.factor === '' ? NaN : Number(band.factor)

    if (!Number.isFinite(factor) || factor < 0 || factor > 1) {
      return `Faixa ${i + 1}: o fator de demanda deve estar entre 0 e 1.`
    }

    if (isLast) continue

    const upTo = band.upTo === '' ? NaN : Number(band.upTo)

    if (!Number.isFinite(upTo) || upTo <= 0) return `Faixa ${i + 1}: informe um limite positivo.`
    if (form.type === 'quantity' && !Number.isSafeInteger(upTo)) {
      return `Faixa ${i + 1}: o limite deve ser um número inteiro de aparelhos.`
    }
    if (upTo <= previous) return `Faixa ${i + 1}: os limites devem ser estritamente crescentes.`

    previous = upTo
  }

  return null
}

function buildRule(id, form) {
  const lastIndex = form.bands.length - 1
  const rule = {
    id,
    name: form.name.trim(),
    type: form.type,
    bands: form.bands.map((band, index) => ({
      upTo: index === lastIndex ? null : Number(band.upTo),
      factor: Number(band.factor),
    })),
  }

  if (form.type === 'power-bands') rule.unit = form.unit

  return rule
}

export function describeRule(rule) {
  if (rule.type === 'quantity') return 'Quantidade de aparelhos'
  return `Faixas de potência (${rule.unit || 'kVA'})`
}

export function DemandRulesEditor({ rules, onSave, onDelete, onRestoreDefaults, isLoading }) {
  const [form, setForm] = useState(null)
  const [editingId, setEditingId] = useState(null)

  const limitLabel = form?.type === 'quantity' ? 'Até (aparelhos)' : `Até (${form?.unit || 'kVA'})`

  function openNew() {
    setEditingId(null)
    setForm({ ...emptyForm, bands: emptyForm.bands.map((band) => ({ ...band })) })
  }

  function openEdit(event, rule) {
    event.preventDefault()
    setEditingId(rule.id)
    setForm(formFromRule(rule))
  }

  function closeForm() {
    setEditingId(null)
    setForm(null)
  }

  function updateForm(changes) {
    setForm((prev) => ({ ...prev, ...changes }))
  }

  function updateBand(index, changes) {
    setForm((prev) => ({
      ...prev,
      bands: prev.bands.map((band, i) => (i === index ? { ...band, ...changes } : band)),
    }))
  }

  function addBand(event) {
    event.preventDefault()

    setForm((prev) => {
      if (prev.bands.length >= MAX_BANDS) return prev

      const bands = [...prev.bands]
      bands.splice(bands.length - 1, 0, { upTo: '', factor: '1' })

      return { ...prev, bands }
    })
  }

  function removeBand(event, index) {
    event.preventDefault()

    setForm((prev) => (prev.bands.length > 1 ? { ...prev, bands: prev.bands.filter((_, i) => i !== index) } : prev))
  }

  function handleSave(event) {
    event.preventDefault()

    const error = validateForm(form)

    if (error) {
      toast.error(error)
      return
    }

    if (editingId === null && rules.length >= MAX_RULES) {
      toast.error(`Use no máximo ${MAX_RULES} regras.`)
      return
    }

    const id = editingId ?? generateRuleId(form.name, rules)

    onSave(buildRule(id, form), editingId === null)
    closeForm()
  }

  function handleDelete(event, rule) {
    event.preventDefault()
    onDelete(rule.id)

    if (editingId === rule.id) closeForm()
  }

  return (
    <details open={form !== null || undefined} style={{ margin: '16px 0' }}>
      <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '1.1rem' }}>
        Regras de demanda ({rules.length})
      </summary>
      <p style={{ fontSize: '0.85rem', margin: '8px 0 0' }}>
        FD por quantidade de aparelhos ou faixas progressivas de potência, vinculado aos circuitos. Valem apenas para
        este cálculo.
      </p>
      {rules.length < 1 ? (
        <p>{isLoading ? 'Carregando regras padrão...' : 'Nenhuma regra de demanda cadastrada'}</p>
      ) : (
        <TableContainer>
          <Table style={{ marginTop: '10px' }}>
            <thead>
              <Tr>
                <Th>Nome</Th>
                <Th>Tipo</Th>
                <Th>Faixas</Th>
                <Th>Editar</Th>
                <Th>Remover</Th>
              </Tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <Tr key={rule.id}>
                  <Td>{rule.name || rule.id}</Td>
                  <Td>{describeRule(rule)}</Td>
                  <Td>{rule.bands.length}</Td>
                  <Td style={{ textAlign: 'center' }}>
                    <button type="button" onClick={(event) => openEdit(event, rule)}>
                      <FaEdit />
                    </button>
                  </Td>
                  <Td style={{ textAlign: 'center' }}>
                    <button type="button" onClick={(event) => handleDelete(event, rule)}>
                      <FaMinusCircle />
                    </button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      )}

      {form === null && (
        <ActionButtonsRow $isFormValid style={{ marginTop: '10px', marginBottom: '0' }}>
          <button type="button" onClick={openNew}>
            Nova regra
          </button>
          <button type="button" onClick={onRestoreDefaults} disabled={isLoading}>
            Restaurar padrões
          </button>
        </ActionButtonsRow>
      )}

      {form !== null && (
        <>
          <h3>{editingId === null ? 'Nova regra' : `Editar regra: ${editingId}`}</h3>
          <InputDiv>
            <span>Nome</span>
            <input type="text" value={form.name} onChange={(event) => updateForm({ name: event.target.value })} />
          </InputDiv>
          <InputDiv>
            <span>Tipo</span>
            <div>
              <Select
                style={{ marginLeft: '0' }}
                value={form.type}
                onChange={(event) => updateForm({ type: event.target.value })}
              >
                {RULE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            </div>
          </InputDiv>
          {form.type === 'power-bands' && (
            <InputDiv>
              <span>Unidade de potência</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  value={form.unit}
                  onChange={(event) => updateForm({ unit: event.target.value })}
                >
                  <option value="kW">kW</option>
                  <option value="kVA">kVA</option>
                </Select>
              </div>
            </InputDiv>
          )}
          <p>
            {form.type === 'quantity'
              ? 'O FD da faixa em que a quantidade total de aparelhos do circuito se enquadra incide sobre toda a potência do circuito.'
              : 'Cada FD incide apenas sobre a parcela de potência dentro da faixa (limites acumulados).'}{' '}
            A última faixa vale para todo o excedente.
          </p>
          <TableContainer>
            <Table style={{ marginTop: '10px' }}>
              <thead>
                <Tr>
                  <Th>Faixa</Th>
                  <Th>{limitLabel}</Th>
                  <Th>FD (0 a 1)</Th>
                  <Th>Remover</Th>
                </Tr>
              </thead>
              <tbody>
                {form.bands.map((band, index) => {
                  const isLast = index === form.bands.length - 1

                  return (
                    <Tr key={index}>
                      <Td>{index + 1}</Td>
                      <Td>
                        {isLast ? (
                          'Excedente'
                        ) : (
                          <input
                            type="number"
                            min="0"
                            step="any"
                            style={bandInputStyle}
                            value={band.upTo}
                            onChange={(event) => updateBand(index, { upTo: event.target.value })}
                          />
                        )}
                      </Td>
                      <Td>
                        <input
                          type="number"
                          min="0"
                          max="1"
                          step="any"
                          style={bandInputStyle}
                          value={band.factor}
                          onChange={(event) => updateBand(index, { factor: event.target.value })}
                        />
                      </Td>
                      <Td style={{ textAlign: 'center' }}>
                        <button type="button" disabled={form.bands.length < 2} onClick={(e) => removeBand(e, index)}>
                          <FaMinusCircle />
                        </button>
                      </Td>
                    </Tr>
                  )
                })}
              </tbody>
            </Table>
          </TableContainer>
          <InputDiv style={{ marginTop: '10px' }}>
            <span>Adicionar faixa</span>
            <div>
              <AddButton type="button" onClick={addBand} style={{ marginLeft: 0 }}>
                <FaPlusCircle />
              </AddButton>
            </div>
          </InputDiv>
          <ActionButtonsRow $isFormValid style={{ marginTop: '20px', marginBottom: '20px' }}>
            <button type="button" onClick={handleSave}>
              Salvar regra
            </button>
            <button type="button" onClick={closeForm}>
              Cancelar
            </button>
          </ActionButtonsRow>
        </>
      )}
    </details>
  )
}
